import React from "react";
import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(annotationPlugin);

// Configuración de las variables de riesgo:
// campo: nombre del campo en el item individual
// avgKey: nombre del campo AVG en Listado1Total
// sdKey: nombre del campo STDEV en Listado1Total
// flagKey: campo del item con el veredicto de riesgo ("R" = riesgo). Es la
//   MISMA fuente de verdad que Total_Riesgo y la lista de hallazgos, calculada
//   contra la NORMA de referencia (no contra la media del grupo). El color rojo
//   de la barra se decide con este flag; el Z-score solo define el LARGO.
const RIESGO_CONFIG = [
  {
    nombre: "Exceso de Confianza",
    campo: "Confianza",
    avgKey: "AVG_Confianza",
    sdKey: "STDEV_Confianza",
    flagKey: "ECO",
  },
  {
    nombre: "Pesimismo",
    campo: "Pesimismo",
    avgKey: "AVG_Pesimismo",
    sdKey: "STDEV_Pesimismo",
    flagKey: "PES",
    hideWhen: {
      campo: "DM_Optimismo_Pesimismo_Factor",
      valor: "0",
      nombreAlt: "Pesimismo (DM)",
    },
  },
  {
    nombre: "Desesperanza",
    campo: "Desesperanza",
    avgKey: "AVG_Desesperanza",
    sdKey: "STDEV_Desesperanza",
    flagKey: "DES",
    hideWhen: {
      campo: "DM_Esperanza_Desesperanza_Factor",
      valor: "0",
      nombreAlt: "Desesperanza (DM)",
    },
  },
  {
    nombre: "Ineficacia",
    campo: "Ineficacia",
    avgKey: "AVG_Ineficacia",
    sdKey: "STDEV_Ineficacia",
    flagKey: "INE",
    hideWhen: {
      campo: "DM_Autoeficacia_Ineficacia_Factor",
      valor: "0",
      nombreAlt: "Ineficacia (DM)",
    },
  },
  {
    nombre: "Fragilidad",
    campo: "Fragilidad",
    avgKey: "AVG_Fragilidad",
    sdKey: "STDEV_Fragilidad",
    flagKey: "FRA",
    hideWhen: {
      campo: "DM_Resiliencia_Fragilidad_Factor",
      valor: "0",
      nombreAlt: "Fragilidad (DM)",
    },
  },
  {
    nombre: "Cinismo",
    campo: "Cinismo",
    avgKey: "AVG_Cinismo",
    sdKey: "STDEV_Cinismo",
    flagKey: "CIN",
  },
  {
    nombre: "Superstición",
    campo: "Supersticion",
    avgKey: "AVG_Supersticion",
    sdKey: "STDEV_Supersticion",
    flagKey: "SUP",
  },
  {
    nombre: "Influencia Social",
    campo: "Influencia",
    avgKey: "AVG_Influencia",
    sdKey: "STDEV_Influencia",
    flagKey: "ISO",
  },
  {
    nombre: "Sobre valor de sí mismo",
    campo: "Sesgo SobreValoracion",
    avgKey: "AVG_Sesgo_SobreValoracion",
    sdKey: "STDEV_Sesgo_SobreValoracion",
    flagKey: "SSV",
  },
];

/**
 * Transforma los datos del item individual a Z-Scores.
 *
 * La barra se calcula contra la NORMA de referencia (misma base que el flag de
 * riesgo), de modo que una variable marcada como riesgo (rojo) siempre cruza la
 * línea de +0.7 SD. El Z-Score vs el grupo se conserva solo como referencia para
 * el tooltip. Si no llega la norma (norma vacío), se cae al grupo para no romper.
 */
const transformarDatosANormalizados = (item, totales, norma) => {
  return RIESGO_CONFIG.map((variable) => {
    // Si tiene regla hideWhen y la condición se cumple, ocultar la barra
    if (variable.hideWhen) {
      const campoCheck = item[variable.hideWhen.campo];
      if (String(campoCheck) === String(variable.hideWhen.valor)) {
        return {
          nombre: variable.hideWhen.nombreAlt || variable.nombre,
          valorNorm: null,
          valorOriginal: null,
          oculto: true,
          esRiesgo: false,
        };
      }
    }

    const valor = parseFloat(item[variable.campo] || 0);

    // Estadísticas del grupo (lote actual)
    const mediaGrupo = parseFloat(totales[variable.avgKey] || 0);
    const sdGrupo = parseFloat(totales[variable.sdKey] || 1);
    const zGrupo = sdGrupo !== 0 ? (valor - mediaGrupo) / sdGrupo : 0;

    // Estadísticas de la norma de referencia (base oficial del flag).
    const tieneNorma =
      norma &&
      norma[variable.avgKey] != null &&
      norma[variable.sdKey] != null;
    const mediaNorma = tieneNorma ? parseFloat(norma[variable.avgKey]) : mediaGrupo;
    const sdNorma = tieneNorma ? parseFloat(norma[variable.sdKey] || 1) : sdGrupo;
    const zNorma = sdNorma !== 0 ? (valor - mediaNorma) / sdNorma : 0;

    // La barra usa la NORMA (cae al grupo si no hay norma).
    const zBarra = tieneNorma ? zNorma : zGrupo;

    return {
      nombre: variable.nombre,
      valorNorm: parseFloat(zBarra.toFixed(2)),
      valorOriginal: valor,
      // Referencia grupo (lote)
      mediaGrupo,
      sdGrupo,
      zGrupo,
      // Referencia norma (base de la barra y del flag)
      tieneNorma,
      mediaNorma,
      sdNorma,
      zNorma,
      oculto: false,
      // Veredicto de riesgo real (vs norma). Define el color rojo de la barra.
      esRiesgo: String(item[variable.flagKey]) === "R",
    };
  });
};

const GraficoZScore = (props) => {
  const { item, totales, norma } = props;
  const datosNormalizados = transformarDatosANormalizados(item, totales, norma);

  const etiquetas = datosNormalizados.map((d) => d.nombre);
  const valores = datosNormalizados.map((d) => d.valorNorm);

  // Color de barra según el VEREDICTO de riesgo real (flag "R" vs norma), no
  // según el Z-Score vs grupo. Así el rojo es 100% consistente con Total_Riesgo
  // y la lista de hallazgos: una variable marcada como riesgo aparece roja
  // aunque su Z-Score vs la media del grupo no alcance +0.7 SD. El Z-Score solo
  // define el LARGO de la barra (posición vs grupo); para las no-riesgo se
  // mantiene el degradado beige/azul como referencia visual.
  const backgroundColors = datosNormalizados.map((d) =>
    d.esRiesgo
      ? "rgb(198, 107, 106)" // rojo: riesgo marcado (flag "R" vs norma)
      : d.valorNorm >= 0
        ? "rgb(210, 196, 187)" // beige: sobre la media del grupo, sin riesgo
        : "rgba(18, 135, 171, 0.8)", // azul: bajo la media del grupo
  );

  const data = {
    labels: etiquetas,
    datasets: [
      {
        label: "Z-Score",
        data: valores,
        backgroundColor: backgroundColors,
        borderColor: "rgb(200, 200, 200)",
        barPercentage: 0.6,
      },
    ],
  };

  // Construir anotaciones: línea de media (0) y líneas de ±1 SD
  const annotations = {
    mediaLine: {
      type: "line",
      xMin: 0,
      xMax: 0,
      borderColor: "rgba(220, 50, 50, 0.8)",
      borderWidth: 2,
      label: {
        content: "Media",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sdPos: {
      type: "line",
      xMin: 1,
      xMax: 1,
      borderColor: "rgba(255, 165, 0, 0.7)",
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: "+1 SD",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sdNeg: {
      type: "line",
      xMin: -1,
      xMax: -1,
      borderColor: "rgba(255, 165, 0, 0.7)",
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: "-1 SD",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sd07Pos: {
      type: "line",
      xMin: 0.7,
      xMax: 0.7,
      borderColor: "rgba(192, 80, 77, 0.6)",
      borderWidth: 1.5,
      borderDash: [3, 3],
      label: {
        content: "+0.7 SD",
        enabled: true,
        position: "start",
        font: { size: 9 },
      },
    },
    sd07Neg: {
      type: "line",
      xMin: -0.7,
      xMax: -0.7,
      borderColor: "rgba(192, 80, 77, 0.6)",
      borderWidth: 1.5,
      borderDash: [3, 3],
      label: {
        content: "-0.7 SD",
        enabled: true,
        position: "start",
        font: { size: 9 },
      },
    },
  };

  const options = {
    indexAxis: "y",
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: { left: 0 },
    },
    scales: {
      x: {
        min: -2,
        max: 2,
        ticks: {
          stepSize: 1,
          font: { size: 10 },
          callback: (value) => {
            if (value === 0) return "Media";
            return value > 0 ? `+${value} SD` : `${value} SD`;
          },
        },
        title: {
          display: true,
          text: "Desviaciones Estándar (Z-Score)",
          font: { size: 11 },
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          font: { size: 12 },
        },
        afterFit: function (scaleInstance) {
          scaleInstance.width = 180;
        },
      },
    },
    plugins: {
      annotation: {
        annotations: annotations,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const d = datosNormalizados[context.dataIndex];
            if (d.oculto) return "Distorsión Motivacional detectada";
            // El popup muestra solo la base de la barra (norma si está
            // disponible) con etiquetas neutras: la distinción norma/grupo solo
            // se expone a superusuarios en la tabla de detalle por pregunta.
            const media = d.tieneNorma ? d.mediaNorma : d.mediaGrupo;
            const sd = d.tieneNorma ? d.sdNorma : d.sdGrupo;
            const z = d.tieneNorma ? d.zNorma : d.zGrupo;
            return [
              `Valor: ${d.valorOriginal}`,
              `Media: ${media.toFixed(2)}`,
              `SD: ${sd.toFixed(2)}`,
              `Z-Score: ${z > 0 ? "+" : ""}${z.toFixed(2)}σ`,
            ];
          },
        },
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <Bar
      options={options}
      data={data}
      style={{ height: "100%", width: "100%" }}
    />
  );
};

const GraficoZScoreWrapper = (props) => {
  return (
    <div style={{ height: "100%", width: "100%" }}>
      <GraficoZScore {...props} />
    </div>
  );
};

export default GraficoZScoreWrapper;
